import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SOCIAL_CHAT_LOADING, CHAT_MESSAGES_LOADING } from "../../constants/loading.constant";
import { useGlobalCore } from "../globals";
import { socialChatApiService } from "./services/api";
import { setSelectedFacebookConversationAction, setSelectedInstagramConversationAction, setSelectedWhatsappConversationAction } from "./store/actions";
import {
  getFacebookConversationsFailureAction,
  getFacebookConversationsInitAction,
  getFacebookConversationsSuccessAction,
  getFacebookMessagesFailureAction,
  getFacebookMessagesInitAction,
  getFacebookMessagesSuccessAction,
  getInstagramConversationsFailureAction,
  getInstagramConversationsInitAction,
  getInstagramConversationsSuccessAction,
  getInstagramMessagesFailureAction,
  getInstagramMessagesInitAction,
  getInstagramMessagesSuccessAction,
  getWhatsappConversationsFailureAction,
  getWhatsappConversationsInitAction,
  getWhatsappConversationsSuccessAction,
  getWhatsappMessagesFailureAction,
  getWhatsappMessagesInitAction,
  getWhatsappMessagesSuccessAction,
  postFacebookMessageFailureAction,
  postFacebookMessageInitAction,
  postFacebookMessageSuccessAction,
  postFacebookSeenMessageFailureAction,
  postFacebookSeenMessageInitAction,
  postFacebookSeenMessageSuccessAction,
  postInstagramMessageFailureAction,
  postInstagramMessageInitAction,
  postInstagramMessageSuccessAction,
  postWhatsappMessageFailureAction,
  postWhatsappMessageInitAction,
  postWhatsappMessageSuccessAction,
} from "./store/api-actions";
import {
  facebookConversationsSelector,
  facebookMessagesSelector,
  facebookSelectedSelector,
  instagramConversationsSelector,
  instagramMessagesSelector,
  instagramSelectedSelector,
  whatsappConversationsSelector,
  whatsappMessagesSelector,
  whatsappSelectedSelector,
} from "./store/selector";

export function useSocialChatCore() {
  const dispatch = useDispatch();
  const instagramConversations = useSelector(instagramConversationsSelector);
  const instagramSelected = useSelector(instagramSelectedSelector);
  const instagramMessages = useSelector(instagramMessagesSelector);
  const facebookConversations = useSelector(facebookConversationsSelector);
  const facebookSelected = useSelector(facebookSelectedSelector);
  const facebookMessages = useSelector(facebookMessagesSelector);
  const whatsappConversations = useSelector(whatsappConversationsSelector);
  const whatsappSelected = useSelector(whatsappSelectedSelector);
  const whatsappMessages = useSelector(whatsappMessagesSelector);
  const { turnOnLoading, turnOffLoading } = useGlobalCore();
  const getInstagramConversations = useCallback(
    async (companyId: string) => {
      try {
        dispatch(getInstagramConversationsInitAction());
        turnOnLoading(SOCIAL_CHAT_LOADING);
        const response = await socialChatApiService.getInstagramConversations(companyId);
        dispatch(getInstagramConversationsSuccessAction(response));
        turnOffLoading(SOCIAL_CHAT_LOADING);
      } catch (error: any) {
        console.error(error);
        turnOffLoading(SOCIAL_CHAT_LOADING);
        dispatch(getInstagramConversationsFailureAction({ error }));
      }
    },
    [dispatch, turnOnLoading, turnOffLoading]
  );
  const getInstagramMessages = useCallback(
    async (companyId: string, conversationId: string) => {
      try {
        dispatch(getInstagramMessagesInitAction());
        turnOnLoading(CHAT_MESSAGES_LOADING);
        const response = await socialChatApiService.getInstagramMessages(companyId, conversationId);
        dispatch(getInstagramMessagesSuccessAction(response));
        turnOffLoading(CHAT_MESSAGES_LOADING);
      } catch (error: any) {
        console.error(error);
        turnOffLoading(CHAT_MESSAGES_LOADING);
        dispatch(getInstagramMessagesFailureAction({ error }));
      }
    },
    [dispatch, turnOnLoading, turnOffLoading]
  );
  const postInstagramMessage = useCallback(
    async (
      companyId: string,
      conversationId: string,
      recipientId: string,
      message: string
    ) => {
      try {
        dispatch(postInstagramMessageInitAction());
        const response = await socialChatApiService.postInstagramMessage(
          companyId,
          conversationId,
          recipientId,
          message
        );
        dispatch(postInstagramMessageSuccessAction(response));
      } catch (error: any) {
        console.error(error);
        dispatch(postInstagramMessageFailureAction({ error }));
      }
    },
    [dispatch]
  );
  const getFacebookConversations = useCallback(
    async (companyId: string) => {
      try {
        dispatch(getFacebookConversationsInitAction());
        turnOnLoading(SOCIAL_CHAT_LOADING);
        const response = await socialChatApiService.getFacebookConversations(companyId);
        dispatch(getFacebookConversationsSuccessAction(response));
        turnOffLoading(SOCIAL_CHAT_LOADING, 500);
      } catch (error: any) {
        console.error(error);
        turnOffLoading(SOCIAL_CHAT_LOADING);
        dispatch(getFacebookConversationsFailureAction({ error }));
      }
    },
    [dispatch, turnOnLoading, turnOffLoading]
  );
  const getFacebookMessages = useCallback(
    async (companyId: string, conversationId: string) => {
      try {
        dispatch(getFacebookMessagesInitAction());
        turnOnLoading(CHAT_MESSAGES_LOADING);
        const response = await socialChatApiService.getFacebookMessages(companyId, conversationId);
        dispatch(getFacebookMessagesSuccessAction(response));
        turnOffLoading(CHAT_MESSAGES_LOADING);
      } catch (error: any) {
        console.error(error);
        turnOffLoading(CHAT_MESSAGES_LOADING);
        dispatch(getFacebookMessagesFailureAction({ error }));
      }
    },
    [dispatch, turnOnLoading, turnOffLoading]
  );
  const postFacebookMessage = useCallback(
    async (
      companyId: string,
      conversationId: string,
      recipientId: string,
      message: string
    ) => {
      try {
        dispatch(postFacebookMessageInitAction());
        const response = await socialChatApiService.postFacebookMessage(companyId, conversationId, recipientId, message);
        dispatch(postFacebookMessageSuccessAction(response));
      } catch (error: any) {
        console.error(error);
        dispatch(postFacebookMessageFailureAction({ error }));
      }
    },
    [dispatch]
  );
  const postFacebookSeenMessage = useCallback(
    async (companyId: string, conversationId: string, recipientId: string) => {
      try {
        dispatch(postFacebookSeenMessageInitAction());
        const response = await socialChatApiService.postFacebookSeenMessage(companyId, conversationId, recipientId);
        dispatch(postFacebookSeenMessageSuccessAction(response));
      } catch (error: any) {
        console.error(error);
        dispatch(postFacebookSeenMessageFailureAction({ error }));
      }
    },
    [dispatch]
  );
  const getWhatsappConversations = useCallback(
    async (companyId: string) => {
      try {
        dispatch(getWhatsappConversationsInitAction());
        turnOnLoading(SOCIAL_CHAT_LOADING);
        const response = await socialChatApiService.getWhatsappConversations(companyId);
        dispatch(getWhatsappConversationsSuccessAction(response));
        turnOffLoading(SOCIAL_CHAT_LOADING, 500);
      } catch (error: any) {
        console.error(error);
        turnOffLoading(SOCIAL_CHAT_LOADING);
        dispatch(getWhatsappConversationsFailureAction({ error }));
      }
    },
    [dispatch, turnOnLoading, turnOffLoading]
  );
  const getWhatsappMessages = useCallback(
    async (companyId: string, conversationId: string) => {
      try {
        dispatch(getWhatsappMessagesInitAction());
        turnOnLoading(CHAT_MESSAGES_LOADING);
        const response = await socialChatApiService.getWhatsappMessages(companyId, conversationId);
        dispatch(getWhatsappMessagesSuccessAction(response));
        turnOffLoading(CHAT_MESSAGES_LOADING);
      } catch (error: any) {
        console.error(error);
        turnOffLoading(CHAT_MESSAGES_LOADING);
        dispatch(getWhatsappMessagesFailureAction({ error }));
      }
    },
    [dispatch, turnOnLoading, turnOffLoading]
  );
  const postWhatsappMessage = useCallback(
    async (
      companyId: string,
      conversationId: string,
      recipientId: string,
      message: string
    ) => {
      try {
        dispatch(postWhatsappMessageInitAction());
        const response = await socialChatApiService.postWhatsappMessage(companyId, conversationId, recipientId, message);
        dispatch(postWhatsappMessageSuccessAction(response));
      } catch (error: any) {
        console.error(error);
        dispatch(postWhatsappMessageFailureAction({ error }));
      }
    },
    [dispatch]
  );

  return {
    instagramConversations,
    instagramSelected,
    instagramMessages,
    facebookConversations,
    facebookSelected,
    facebookMessages,
    whatsappConversations,
    whatsappSelected,
    whatsappMessages,
    getInstagramConversations,
    getInstagramMessages,
    postInstagramMessage,
    selectInstagramConversation: (id: string | null) =>
      dispatch(setSelectedInstagramConversationAction({ id })),
    getFacebookConversations,
    getFacebookMessages,
    postFacebookMessage,
    postFacebookSeenMessage,
    selectFacebookConversation: (id: string | null) =>
      dispatch(setSelectedFacebookConversationAction({ id })),
    getWhatsappConversations,
    getWhatsappMessages,
    postWhatsappMessage,
    selectWhatsappConversation: (id: string | null) =>
      dispatch(setSelectedWhatsappConversationAction({ id })),
  };
}
